import { useTranslation } from "@/language"
/**
 * IncomeSummary
 * Displays total income, pending income, available for payout, and a payout button.
 */

import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type { EarningsSummary } from "../services/earnings.service"

type IncomeSummaryProps = {
  summary?: EarningsSummary
  isLoading: boolean
  isRequestingPayout: boolean
  onPayout: () => void
}

function formatCurrency(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount)
}

export function IncomeSummary({
  summary,
  isLoading,
  isRequestingPayout,
  onPayout,
}: IncomeSummaryProps) {
  const { t } = useTranslation("settings")
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false)

  // Helper to format date and time
  function formatDateTime(date: Date): string {
    const datePart = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date)

    const timePart = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date)

    return `${datePart} – ${timePart}`
  }

  if (isLoading) {
    return <IncomeSummarySkeleton />
  }

  const totalIncome = summary?.totalIncome ?? 0
  const pendingIncome = summary?.pendingIncome ?? 0
  const available = summary?.availableForPayout ?? 0

  const handlePayoutClick = () => {
    setIsPayoutModalOpen(true)
  }

  const handleConfirmPayout = () => {
    setIsPayoutModalOpen(false)
    onPayout()
  }

  return (
    <div className="flex items-start justify-between">
      <div className="w-full">
        <div className="flex flex-col md:flex-row lg:flex-col xl:flex-row  md:justify-between w-full items-start md:items-end lg:items-start xl:items-end gap-3.75 md:gap-5 lg:gap-3.75 xl:gap-5">
          <div className="space-y-2.5 w-full xl:w-[278px]">
            <h3 className="text-[15px] font-semibold text-primary uppercase mb-5 leading-6.25">
              Income Summary
            </h3>
            <SummaryRow label="Total income" value={formatCurrency(totalIncome)} large />
            <SummaryRow label="Pending income" value={formatCurrency(pendingIncome)} />
            <SummaryRow label="Available for payout" value={formatCurrency(available)} />
          </div>
          <div className="flex flex-col-reverse md:flex-row lg:flex-col-reverse xl:flex-row items-center gap-3.75 xl:justify-end w-full">
            <span className="text-sm text-body">Minimum payout: {formatCurrency(available)}</span>
            <Button
              variant="outline"
              size="sm"
              className="h-[50px] w-full md:w-[150px] lg:w-full xl:w-[150px] px-11 py-3.75 uppercase border-white disabled:bg-[#F2F0EB26] disabled:border-0 disabled:text-[#020202]! cursor-pointer"
              onClick={handlePayoutClick}
              disabled={available === 0}
            >
              Payout
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isPayoutModalOpen} onOpenChange={setIsPayoutModalOpen}>
        <DialogContent className="sm:max-w-[600px] flex flex-col gap-7.5 p-0 pb-[40px] pt-[60px]">
          <DialogHeader className="flex flex-col gap-2.5">
            <DialogTitle className="text-2xl text-center font-semibold leading-normal text-primary">
              Payout sent
            </DialogTitle>
            <DialogDescription className="text-primary text-center text-sm font-normal leading-6.25">
              Your payout has been successfully transferred to your Stripe account
            </DialogDescription>
          </DialogHeader>
          {/* Receipt Details */}
          <div className="p-5 bg-[#5A5B3F26] border border-brand-olive rounded-[15px] ml-[58px] mr-[57px] flex flex-col gap-2.5">
            {/* Account Sent */}
            <div className="flex items-center gap-2.5">
              <span>
                <img
                  src={`${import.meta.env.VITE_ASSET_BASE_URL}receipt.svg`}
                  alt=""
                  className="w-4 h-4 inline-block"
                />
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm leading-6 text-primary">
                  {t("transactions.incomeSummary.accountSent")}
                </span>
                <span className="text-sm leading-6 font-semibold text-primary">
                  {formatCurrency(available)}
                </span>
              </div>
            </div>

            {/* Rituality Fee */}
            <div className="flex items-center gap-2.5">
              <span>
                <img
                  src={`${import.meta.env.VITE_ASSET_BASE_URL}money_calc.svg`}
                  alt=""
                  className="w-4 h-4 inline-block"
                />
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm leading-6 text-primary">
                  {t("transactions.incomeSummary.ritualityFee")}
                </span>
                <span className="text-sm leading-6 font-semibold text-primary">
                  {formatCurrency(0)}
                </span>
              </div>
            </div>

            {/* Total Processed */}
            <div className="flex items-center gap-2.5">
              <span>
                <img
                  src={`${import.meta.env.VITE_ASSET_BASE_URL}money.svg`}
                  alt=""
                  className="w-4 h-4 inline-block"
                />
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm leading-6 text-primary">
                  {t("transactions.incomeSummary.totalProcessed")}
                </span>
                <span className="text-sm leading-6 font-semibold text-primary">
                  {formatCurrency(available)}
                </span>
              </div>
            </div>

            {/* Destination */}
            <div className="flex items-center gap-2.5">
              <span>
                <img
                  src={`${import.meta.env.VITE_ASSET_BASE_URL}credit_card.svg`}
                  alt=""
                  className="w-4 h-4 inline-block"
                />
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm leading-6 text-primary">
                  {t("transactions.incomeSummary.destination")}
                </span>
                <span className="text-sm leading-6 text-primary font-semibold">
                  Stripe **** ----
                </span>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-center gap-2.5">
              <span>
                <img
                  src={`${import.meta.env.VITE_ASSET_BASE_URL}calendar.svg`}
                  alt=""
                  className="w-4 h-4 inline-block"
                />
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm leading-6 text-primary">
                  {t("transactions.incomeSummary.date")}
                </span>
                <span className="text-sm leading-6 text-primary">{formatDateTime(new Date())}</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3.75 flex flex-col! items-center w-full">
            <Button
              onClick={handleConfirmPayout}
              disabled={isRequestingPayout}
              isLoading={isRequestingPayout}
              className="max-w-[280px] w-full py-3.75 uppercase"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function IncomeSummarySkeleton() {
  return (
    <div className="flex items-start justify-between">
      <div className="w-full">
        <div className="flex justify-between items-end">
          <div className="space-y-5 max-w-[278px]">
            <Skeleton className="h-[22px] w-[130px]" />
            <SummaryRowSkeleton valueWidth="w-[90px]" />
            <SummaryRowSkeleton valueWidth="w-[70px]" />
            <SummaryRowSkeleton valueWidth="w-[100px]" />
          </div>
          <div className="flex items-center gap-3.75">
            <Skeleton className="h-[18px] w-[160px]" />
            <Skeleton className="h-[50px] w-[100px]" />
          </div>
        </div>
      </div>
    </div>
  )
}

function SummaryRow({ label, value, large }: { label: string; value: string; large?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-20 w-full">
      <span className="text-[14px] text-[#F2F0EBB3]">{label}</span>
      <span
        className={cn(
          "font-semibold leading-6.25 text-primary",
          large ? "text-[18px]" : "text-[14px]",
        )}
      >
        {value}
      </span>
    </div>
  )
}

function SummaryRowSkeleton({ valueWidth }: { valueWidth?: string }) {
  return (
    <div className="flex items-center justify-between gap-20 w-full">
      <Skeleton className="h-[16px] w-[120px]" />
      <Skeleton className={cn("h-[18px]", valueWidth ?? "w-[70px]")} />
    </div>
  )
}
